import type { Config } from "@wagmi/core"
import type { Address } from "viem"

import type { IStorageBackend } from "@/domains/common/storage"
import type { IEventBus } from "@/domains/events"
import type { IPublicClientGetter } from "@/domains/library"
import type { LockOnChainTransportAddresses } from "@/domains/lock/lock-on-chain-transport"
import type { LockApiData } from "@/domains/lock/types"
import type { ITokenLookup } from "@/domains/tokens/types"
import type { IVenammStore } from "@/domains/venamm"
import type { IVoteOnChainTransport } from "@/domains/vote/interfaces"
import { LcAddress } from "@/domains/core"
import { OrchestratedQuery, OrchestratedQueryStore } from "@/domains/library"
import { LockRewardRepository, LockRewardsOnChainTransport } from "@/domains/lock-rewards"
import { LockApiTransport } from "@/domains/lock/lock-api-transport"
import { LockOnChainTransport } from "@/domains/lock/lock-on-chain-transport"
import { LockOrchestrator } from "@/domains/lock/lock-orchestrator"
import { LockRepository } from "@/domains/lock/lock-repository"
import { WalletConnectionStore } from "@/domains/wallet"

import type { ManagerNamespace } from "./types"
import { ManagerAddressSource } from "./manager-address-source"
import { ManagerApprovalOrchestrator } from "./manager-approval-orchestrator"
import { ManagerStore } from "./manager-store"
import { ManagerWalletEventStrategy } from "./manager-wallet-event-strategy"

export type ManagerNamespaceFactoryParams = {
  config: Config
  publicClientGetter: IPublicClientGetter
  lockOnChainTransportAddresses: LockOnChainTransportAddresses
  lockRewardRepository: LockRewardRepository
  lockRewardsOnChainTransport: LockRewardsOnChainTransport
  orchestratedQueryStore: OrchestratedQueryStore
  tokenLookup: ITokenLookup
  wallet: WalletConnectionStore
  eventBus: IEventBus
  storage: IStorageBackend
  chainSlug: string
  chainId: number
  superVoterAddress: Address
  venammStore: IVenammStore
  voteOnChainTransport: IVoteOnChainTransport
}

export const ManagerNamespaceFactory = {
  create({
    config,
    lockOnChainTransportAddresses,
    lockRewardRepository,
    lockRewardsOnChainTransport,
    orchestratedQueryStore,
    tokenLookup,
    wallet,
    eventBus,
    storage,
    chainSlug,
    chainId,
    superVoterAddress,
    venammStore,
    voteOnChainTransport,
  }: ManagerNamespaceFactoryParams): ManagerNamespace {
    // Extract token decimals from token lookup
    const governanceToken = tokenLookup.find(new LcAddress(lockOnChainTransportAddresses.governanceToken))
    const governanceTokenDecimals = governanceToken?.decimals ?? 18
    const vestedTokenDecimals = 18

    // Create separate instances for manager domain
    const lockOnChainTransport = new LockOnChainTransport(
      config,
      lockOnChainTransportAddresses,
      governanceTokenDecimals,
      vestedTokenDecimals,
    )

    const lockApiTransport = new LockApiTransport(lockOnChainTransport)
    const lockRepository = new LockRepository(eventBus)

    // Create a temporary orchestrator reference that we'll set later
    let lockOrchestrator: LockOrchestrator

    // Create manager store (orchestrator will be set after creation)
    const managerStore = new ManagerStore(
      storage,
      chainSlug,
      wallet,
      lockRepository,
      null as unknown as LockOrchestrator,
      eventBus,
    )

    // Create manager address source with the store
    const addressSource = new ManagerAddressSource(managerStore, wallet)

    // Create orchestrator with manager address source and manager wallet event strategy
    // Disable rewards for manager locks - managers can only vote, not claim rewards
    lockOrchestrator = new LockOrchestrator(
      lockRepository,
      lockApiTransport,
      lockOnChainTransport,
      wallet,
      lockRewardsOnChainTransport,
      lockRewardRepository,
      tokenLookup,
      orchestratedQueryStore,
      eventBus,
      voteOnChainTransport,
      addressSource,
      "Manager",
      new ManagerWalletEventStrategy(managerStore), // Manager orchestrator uses manager strategy
      false, // Disable rewards for manager locks - managers can only vote, not claim
    )

    // Update store's orchestrator reference
    managerStore.setLockOrchestrator(lockOrchestrator)

    // Create queries for manager orchestrator
    const loadLocksQuery = new OrchestratedQuery<Array<LockApiData>>(
      "Manager Lock NFTs Sync",
      (query) => lockOrchestrator.onApiLoadLocksTrigger(query),
      1_800_000, // 30 minutes
      lockOrchestrator.onApiLoadLocksQuerySuccess.bind(lockOrchestrator),
      lockOrchestrator.onApiLoadLocksQueryFailed.bind(lockOrchestrator),
      orchestratedQueryStore,
    )

    const loadLockInfoQuery = new OrchestratedQuery<bigint[]>(
      "Manager Lock Info On-Chain Sync",
      (query) => lockOrchestrator.onChainLoadLockInfoTrigger(query),
      1_800_000, // 30 minutes
      lockOrchestrator.onChainLoadLockInfoQuerySuccess.bind(lockOrchestrator),
      lockOrchestrator.onChainLoadLockInfoQueryFailed.bind(lockOrchestrator),
      orchestratedQueryStore,
    )

    const loadVotingInfoQuery = new OrchestratedQuery<any>(
      "Manager Voting Info On-Chain Sync",
      (query) => lockOrchestrator.onChainLoadVotingInfoTrigger(query),
      1_800_000, // 30 minutes
      lockOrchestrator.onLoadVotingInfoQuerySuccess.bind(lockOrchestrator),
      lockOrchestrator.onLoadVotingInfoQueryFailed.bind(lockOrchestrator),
      orchestratedQueryStore,
    )

    // Initialize orchestrator with queries
    lockOrchestrator.initialize(loadLocksQuery, loadLockInfoQuery, loadVotingInfoQuery)

    // Initialize manager store
    managerStore.initialize()

    // Create manager approval orchestrator
    const approvalOrchestrator = new ManagerApprovalOrchestrator(
      managerStore,
      wallet,
      config,
      chainId,
      superVoterAddress,
      orchestratedQueryStore,
      eventBus,
    )
    approvalOrchestrator.initialize()

    // Set approval orchestrator reference in store so it can trigger checks
    managerStore.setApprovalOrchestrator(approvalOrchestrator)

    // Only start queries if we're actually in manager mode
    // Don't start if just a wallet is connected - that's handled by the normal lock orchestrator
    if (managerStore.isManagerMode && loadLocksQuery) {
      loadLocksQuery.start()
    }

    return {
      Store: managerStore,
      Lock: {
        Repository: lockRepository,
        Orchestrator: lockOrchestrator,
      },
      ApprovalOrchestrator: approvalOrchestrator,
    }
  },
}
